'use client';

import { SessionsLogo } from '@askable/ui/icons';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useViewerContext } from 'src/context/ViewerContext/ViewerContext';

import { useIsKinde } from '@/hooks/useIsKinde/useIsKinde';

import { HelpMenu } from './AllLayout/components/HelpMenu';
import { SignInButton } from './AllLayout/components/SignInButton';
import { UserMenu } from './AllLayout/components/UserMenu';

import type { FC, PropsWithChildren } from 'react';

type Props = {
  showLoginHint?: boolean;
  disableHeaderLogo?: boolean;
  hideHeaderRightItems?: boolean;
};

const Logo = ({ disableHeaderLogo }: Pick<Props, 'disableHeaderLogo'>) => {
  const router = useRouter();
  if (disableHeaderLogo) {
    return (
      <div className="ml-2">
        <SessionsLogo />
      </div>
    );
  }
  const handleLogoClick = () => {
    router.replace('/');
  };

  return (
    <Link aria-label="Return home" href="/" onClick={handleLogoClick}>
      <SessionsLogo />
    </Link>
  );
};

const Header = ({ disableHeaderLogo, hideHeaderRightItems, showLoginHint = false }: Props) => {
  const { status } = useViewerContext();
  const isKinde = useIsKinde();

  return (
    <div className="flex w-full items-center justify-between gap-2 p-4 md:p-6">
      <Logo disableHeaderLogo={disableHeaderLogo} />
      {!hideHeaderRightItems && (
        <div className="flex items-center space-x-2">
          {status === 'authenticated' ? <HelpMenu /> : null}
          {status === 'authenticated' ? <UserMenu /> : null}
          {status === 'unauthenticated' && isKinde ? <SignInButton showLoginHint={showLoginHint} /> : null}
        </div>
      )}
    </div>
  );
};

export const MainLayout: FC<PropsWithChildren<Props>> = ({
  children,
  disableHeaderLogo,
  hideHeaderRightItems,
  showLoginHint,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-auto w-full flex-col lg:h-dvh">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-2">
        {t('global.skipToContent')}
      </a>
      <Header
        disableHeaderLogo={disableHeaderLogo}
        hideHeaderRightItems={hideHeaderRightItems}
        showLoginHint={showLoginHint}
      />
      <main id="main-content" className="mx-auto h-full w-full max-w-screen-xl p-4 md:p-6 lg:p-10">
        {children}
      </main>
    </div>
  );
};
